import { toJS } from 'mobx'
import type { ReactNode } from 'react'

import { Button } from '@/Core/UI/Button'
import { isFormDirty } from '@/Ship/Helpers/formDirty'

import { AppDrawer, DEFAULT_DETAIL_WIDTH } from './AppDrawer'
import { DrawerDirtyGuard } from './DrawerDirtyGuard'

interface FormDrawerProps {
    title: ReactNode
    open: boolean
    onClose: () => void
    /** Контекст форми (createFormContext): з нього — submit і перевірка незбережених змін */
    formContext: any
    /** Підпис кнопки дії в шапці */
    submitLabel: string
    /** Іконка кнопки дії (у картках ресурсів «Зберегти» теж з іконкою) */
    submitIcon?: ReactNode
    submitting?: boolean
    /** Дію виконано: кнопка вимкнена з підписом `submittedLabel` */
    submitted?: boolean
    submittedLabel?: string
    width?: number
    children: ReactNode
}

/** Розмір іконки кнопки дії — як у «Зберегти» в картках ресурсів (Core задає 19px) */
const SUBMIT_ICON_SIZE = 19

/** Чи є у формі введені й не відправлені дані */
const formHasChanges = (formContext: any): boolean => {
    const values = toJS(formContext.values) ?? {}
    return isFormDirty(values, toJS(formContext.initialValues), Object.keys(values))
}

/**
 * Drawer з власною формою (не картка ресурсу): те саме оформлення, що й картки ресурсів
 * (AppDrawer, 560px, шапка з кнопкою дії), і підтвердження закриття, якщо щось уже введено.
 * Вміст — форма на FormSection / FormRow.
 */
export const FormDrawer = ({
    title,
    open,
    onClose,
    formContext,
    submitLabel,
    submitIcon,
    submitting = false,
    submitted = false,
    submittedLabel,
    width = DEFAULT_DETAIL_WIDTH,
    children,
}: FormDrawerProps) => (
    <AppDrawer
        title={title}
        placement="right"
        size={width}
        open={open}
        onClose={onClose}
        destroyOnHidden={false}
        extra={
            // primary — як «Зберегти» в картках ресурсів
            <Button
                type="primary"
                icon={
                    submitIcon && (
                        <span style={{ display: 'inline-flex', fontSize: SUBMIT_ICON_SIZE }}>{submitIcon}</span>
                    )
                }
                onClick={() => formContext.submit()}
                loading={submitting}
                disabled={submitted}
            >
                {submitted && submittedLabel ? submittedLabel : submitLabel}
            </Button>
        }
    >
        {open && <DrawerDirtyGuard isDirty={() => formHasChanges(formContext)} />}
        {children}
    </AppDrawer>
)
